import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { checkIsDev } from 'shared/utils/check-is-dev';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { TabMenu } from 'ud-ui/components/tab-menu';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import Account from './tabs/account';
import Experiments from './tabs/experiments/experiments';
import Profile from './tabs/profile/profile';
import SocialNet from './tabs/social-net/social-net';
import Subscription from './tabs/subscription/subscription';

interface SettingsTab {
  /** Дескриптор, а не строка: константа модуля считается до выбора языка, переводится в рендере. */
  name: MessageDescriptor;
  /** Адрес вкладки из `Pages`. */
  path: string;
  /** Хвост адреса, по которому вкладка считается активной; у первой его нет. */
  match?: string;
}

const BASE_TABS: SettingsTab[] = [
  { name: msg`Профиль`, path: Pages.SETTINGS.INDEX },
  { name: msg`Ссылки`, path: Pages.SETTINGS.SOCIAL_NET, match: '/social-net' },
  { name: msg`Подписка`, path: Pages.SETTINGS.SUBSCRIPTION, match: '/subscription' },
  { name: msg`Личный кабинет`, path: Pages.SETTINGS.ACCOUNT, match: '/account' },
];

const EXPERIMENTS_TAB: SettingsTab = { name: msg`Эксперименты`, path: Pages.SETTINGS.EXPERIMENTS, match: '/experiments' };

const SettingsPage = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const location = useLocation();

  // Вкладка и её роут существуют только вне прода: `checkIsDev()` там всегда false.
  const isDev = useMemo(() => checkIsDev(), []);
  const tabs = useMemo(() => (isDev ? [...BASE_TABS, EXPERIMENTS_TAB] : BASE_TABS), [isDev]);

  const activeTab = useMemo(() => {
    const index = tabs.findIndex((tab) => tab.match && location.pathname.includes(tab.match));

    return index === -1 ? 0 : index;
  }, [location.pathname, tabs]);

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Настройки</Trans>
      </UDHeaderTitle>,
    );

    return () => {
      setHeaderContent(null);
    };
  }, [setHeaderContent]);

  const changeTab = (tab: number) => {
    navigate(tabs[tab]?.path ?? Pages.SETTINGS.INDEX);
  };

  return (
    <>
      {/* Заголовок «Информация об организаторе» переехал во вкладку «Профиль»: над кабинетом он неверен. */}
      <div className="pt-6">
        <TabMenu tabs={tabs.map((tab) => ({ name: t(tab.name), active: true }))} activeTab={activeTab} onChange={changeTab} />
      </div>
      <div className="w-full px-6 py-6">
        <Routes>
          <Route path="/" element={<Profile />} />
          <Route path="/social-net" element={<SocialNet />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/account" element={<Account />} />
          {isDev && <Route path="/experiments" element={<Experiments />} />}
        </Routes>
      </div>
    </>
  );
};

export default SettingsPage;
